import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/casino.css';
import { useLocale } from '../../context';
import { staticAsset } from '../../data/staticAssets.js';
import { useCasinoGameFeed, prefetchGameIcons } from '../../hooks/useCasinoGameFeed';
import { lockScroll, unlockScroll } from '../../scrollLock';
import CasinoGameCard from './CasinoGameCard';
import { IconSearch } from './CasinoGameIcons';

const RECOMMENDED_ICON = staticAsset('/content/Casino/categories/gr8/tab-top.png');
const MODAL_PAGE_SIZE = 20;
const SEARCH_EAGER_COUNT = 10;

function IconClose() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <path
        fill="currentColor"
        d="M.456.454a1.094 1.094 0 0 1 1.548 0L9 7.468 16 .454A1.094 1.094 0 0 1 17.546 2l-7.014 6.998 7.015 6.998a1.087 1.087 0 0 1 0 1.545l-.002.003a1.09 1.09 0 0 1-1.545 0l-6.999-7.016-6.998 7.015v.001a1.09 1.09 0 0 1-1.545 0l-.003-.003a1.087 1.087 0 0 1 0-1.544l.001-.001 7.015-6.998L.456 2a1.094 1.094 0 0 1 0-1.547"
      />
    </svg>
  );
}

export default function CasinoSearchModal({ onClose, onNavigate }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const isFiltered = Boolean(debouncedQuery.trim());
  const feed = useCasinoGameFeed({
    category: isFiltered ? 'all' : 'recommended',
    q: debouncedQuery,
    sort: 'popular',
    pageSize: MODAL_PAGE_SIZE,
  });

  const displayGames = feed.games;

  const sectionLabel = isFiltered ? tr('casino.side.games') : tr('casino.side.recommended');

  useEffect(() => {
    if (!displayGames.length) return undefined;
    prefetchGameIcons(displayGames, SEARCH_EAGER_COUNT, 'md');
    return undefined;
  }, [displayGames]);

  useEffect(() => {
    lockScroll();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const tId = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(tId);
      unlockScroll();
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = loadMoreRef.current;
    if (!root || !sentinel || !feed.hasMore || feed.loading || feed.loadingMore) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) feed.loadMore();
      },
      { root, rootMargin: '240px 0px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [feed.hasMore, feed.loading, feed.loadingMore, feed.loadMore, displayGames.length]);

  const openGame = (page, opts) => {
    onClose();
    onNavigate?.(page, opts);
  };

  return createPortal(
    <div className="casino-search-modal__overlay" onClick={onClose}>
      <div
        className="casino-search-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="casino-search-modal-title"
      >
        <button type="button" className="casino-search-modal__close" onClick={onClose} aria-label="Close">
          <IconClose />
        </button>

        <div className="casino-search-modal__scroll" ref={scrollRef}>
          <div className="casino-search-modal__inner">
            <h2 id="casino-search-modal-title" className="casino-search-modal__caption">
              {tr('casino.searchModal.title')}
            </h2>

            <div className="casino-search-modal__toolbar">
              <div className="casino-search-modal__search-field">
                <span className="casino-search-modal__search-icon" aria-hidden="true">
                  <IconSearch />
                </span>
                <input
                  ref={inputRef}
                  type="search"
                  name="search"
                  className="casino-search-modal__search-input"
                  placeholder={tr('casino.searchModal.placeholder')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            </div>

            <div className="casino-search-modal__results-scroll">
              <div className="casino-search-modal__category">
                <i
                  className="casino-search-modal__category-icon"
                  style={{ backgroundImage: `url(${RECOMMENDED_ICON})` }}
                  aria-hidden="true"
                />
                <span className="casino-search-modal__category-label">{sectionLabel}</span>
              </div>

              {feed.loading ? (
                <div className="casino-search-modal__loading" aria-busy="true">
                  <span className="casino-search-modal__loading-spinner" />
                </div>
              ) : displayGames.length === 0 ? (
                <p className="casino-search-modal__empty">{tr('casino.side.noGames')}</p>
              ) : (
                <>
                  <div className="casino-search-modal__grid">
                    {displayGames.map((game, index) => (
                      <CasinoGameCard
                        key={game.id ?? `${game.name}-${index}`}
                        game={game}
                        onNavigate={openGame}
                        iconSize="sm"
                        eagerImage={index < SEARCH_EAGER_COUNT}
                        spinnerWhileLoading
                      />
                    ))}
                  </div>
                  {(feed.hasMore || feed.loadingMore) && (
                    <div
                      ref={loadMoreRef}
                      className="casino-search-modal__load-more"
                      aria-hidden={!feed.loadingMore}
                    >
                      {feed.loadingMore && (
                        <div className="casino-search-modal__loading casino-search-modal__loading--inline" aria-busy="true">
                          <span className="casino-search-modal__loading-spinner" />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
